import type * as React from "react";

import { CalendarIcon } from "@lucide/icons";

import { fromParts } from "../dateAdapter";
import YearPicker, { type YearPickerOnChangeData } from "../pickers/YearPicker";
import InputView from "../views/InputView";
import BaseInput, {
  type BaseInputProps,
  type BaseInputState,
  type DateRelatedProps,
  type DisableValuesProps,
  type MinMaxValueProps,
} from "./BaseInput";
import { buildValue, parseArrayOrValue, parseValue } from "./parse";

export type YearInputProps = BaseInputProps &
  DateRelatedProps &
  MinMaxValueProps &
  DisableValuesProps;

export type YearInputOnChangeData = YearInputProps;

class YearInput extends BaseInput<YearInputProps, BaseInputState> {
  public static readonly defaultProps = {
    ...BaseInput.defaultProps,
    dateFormat: "yyyy",
    icon: CalendarIcon,
  };

  constructor(props: YearInputProps) {
    super(props);
    this.state = { popupIsClosed: true };
  }

  public render() {
    const {
      value,
      disable,
      maxDate,
      minDate,
      initialDate,
      dateFormat,
      closable,
      localization,
      ...rest
    } = this.props;

    return (
      <InputView
        popupIsClosed={this.state.popupIsClosed}
        closePopup={this.closePopup}
        openPopup={this.openPopup}
        {...rest}
        value={value}
        onMount={this.onInputViewMount}
        renderPicker={this.getPicker}
      />
    );
  }

  private getPicker = () => {
    const {
      value,
      disable,
      maxDate,
      minDate,
      initialDate,
      dateFormat,
      localization,
    } = this.props;

    return (
      <YearPicker
        isPickerInFocus={this.isPickerInFocus}
        isTriggerInFocus={this.isTriggerInFocus}
        inline={this.props.inline}
        onCalendarViewMount={this.onCalendarViewMount}
        closePopup={this.closePopup}
        onChange={this.handleSelect as any}
        initializeWith={buildValue(
          value,
          initialDate,
          localization,
          dateFormat,
        )}
        value={buildValue(value, null, localization, dateFormat, null)}
        disable={parseArrayOrValue(disable, dateFormat, localization)}
        maxDate={parseValue(maxDate, dateFormat, localization)}
        minDate={parseValue(minDate, dateFormat, localization)}
        onHeaderClick={() => undefined}
      />
    );
  };

  private handleSelect = (
    e: React.SyntheticEvent<HTMLElement> | null,
    { value }: YearPickerOnChangeData,
  ) => {
    const { localization } = this.props;
    const date = localization
      ? fromParts({ year: value.year }, localization)
      : fromParts({ year: value.year });
    let output = "";
    if (date.isValid()) {
      output = date.format(this.props.dateFormat!);
    }
    const data = { ...this.props, value: output };
    this.props.onChange(e!, data);
    if (this.props.closable) {
      this.closePopup();
    }
  };
}

export default YearInput;
